import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';

const BLOG_URL = '/data/blog.json';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  featured_image?: string;
  tags?: string[];
  published_at: string;
}

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const BlogListPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(BLOG_URL)
      .then(r => r.json())
      .then(data => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20">
      <Section title="Roofing Tips & News" bg="bg-slate-50">
        <p className="text-center text-lg text-gray-700 mb-12 max-w-3xl mx-auto">
          Expert advice on roof maintenance, storm prep, and making the best choices for your Florida home.
        </p>

        {loading && (
          <div className="text-center py-16">
            <div className="inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Blog posts coming soon!</p>
            <p className="text-gray-400 mt-2">Check back for roofing tips, maintenance guides, and local news.</p>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="max-w-4xl mx-auto space-y-8">
            {posts.map((post) => (
              <article
                key={post.slug}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate('/blog/' + post.slug)}
              >
                <div className="sm:flex">
                  {post.featured_image && (
                    <div className="sm:w-64 sm:flex-shrink-0">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-48 sm:h-full object-cover"
                        onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <span>{formatDate(post.published_at)}</span>
                      <span>·</span>
                      <span>{post.author}</span>
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 line-clamp-2 mb-3">{post.excerpt}</p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {post.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="text-center mt-16">
          <Button onClick={() => navigate('/request-quote')} variant="secondary">
            Get a Free Quote
          </Button>
        </div>
      </Section>
    </div>
  );
};

export default BlogListPage;
